import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Hammer, Lock, Mail } from "lucide-react";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/components/auth/AuthProvider";
import { invokeProtectedFunction } from "@/lib/invokeProtectedFunction";
import { trackClientEvent } from "@/lib/tracking/client";
import { readFirstTouch } from "@/lib/tracking/firstTouch";
import { trackTikTokClientEvent } from "@/lib/tracking/tiktokClient";
import { ThemeToggle } from "@/components/ui/theme-toggle";

type Mode = "sign-in" | "sign-up";

const PASSWORD_POLICY_PATTERNS = [
  /password should be at least \d+ characters/i,
  /password should contain at least one character of each/i,
];

function parseAuthError(rawMessage: string, mode: Mode) {
  const isPasswordPolicyError =
    mode === "sign-up" && PASSWORD_POLICY_PATTERNS.some((pattern) => pattern.test(rawMessage));

  if (isPasswordPolicyError) {
    return {
      message:
        "Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, and one number.",
      isPasswordPolicyError: true,
    };
  }

  return {
    message: rawMessage,
    isPasswordPolicyError: false,
  };
}

export default function Auth() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialMode = (searchParams.get("mode") === "sign-up" ? "sign-up" : "sign-in") as Mode;
  const linkError = searchParams.get("error") === "link_expired";
  const nextPath = (() => {
    const next = searchParams.get("next")?.trim();
    if (!next || !next.startsWith("/")) return "/";
    return next;
  })();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(
    linkError ? "Your sign-in link is invalid or has expired. Please request a new one." : null,
  );
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaFactorId, setMfaFactorId] = useState("");
  const [mfaFactors, setMfaFactors] = useState<Array<{ id: string; label: string }>>([]);

  const title = useMemo(() => (mode === "sign-in" ? "Sign in" : "Create account"), [mode]);

  useEffect(() => {
    if (user) {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, nextPath, user]);

  const loadMfaFactors = async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    const verifiedTotpFactors = (data?.totp ?? []).map((factor) => ({
      id: factor.id,
      label: factor.friendly_name || `Authenticator (${factor.id.slice(0, 8)})`,
    }));
    if (verifiedTotpFactors.length === 0) {
      throw new Error("No verified TOTP factors found for this account.");
    }
    setMfaFactors(verifiedTotpFactors);
    setMfaFactorId((prev) => prev || verifiedTotpFactors[0].id);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setShowPasswordRequirements(false);
    setMessage(null);
    setMfaRequired(false);
    setMfaCode("");
    setMfaFactorId("");
    setMfaFactors([]);

    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    if (mode === "sign-up" && password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      if (mode === "sign-in") {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) {
          const code = (signInError as { code?: string }).code ?? "";
          const requiresMfa = code === "mfa_required" || /mfa/i.test(signInError.message);
          if (requiresMfa) {
            try {
              await loadMfaFactors();
              setMfaRequired(true);
              setMessage("Multi-factor verification required. Enter your authenticator code to continue.");
              return;
            } catch (mfaError) {
              const mfaMessage = mfaError instanceof Error ? mfaError.message : "MFA verification is required.";
              setError(mfaMessage);
              return;
            }
          }
          const parsedError = parseAuthError(signInError.message, mode);
          setError(parsedError.message);
          setShowPasswordRequirements(parsedError.isPasswordPolicyError);
          return;
        }
        trackClientEvent("login_completed", { flow: "email" });
        navigate(nextPath, { replace: true });
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: displayName || null,
            },
          },
        });
        if (signUpError) {
          const parsedError = parseAuthError(signUpError.message, mode);
          setError(parsedError.message);
          setShowPasswordRequirements(parsedError.isPasswordPolicyError);
          return;
        }
        const isNewUser = (data.user?.identities?.length ?? 0) > 0;

        if (!isNewUser) {
          setError("An account with this email already exists. Please sign in instead.");
          return;
        }

        if (data.session) {
          // Identify BEFORE the server signup fires so the user-created-log
          // server event (the single authoritative signup_completed) forwards to
          // the same identified PostHog person. Never fall back to `email` as a
          // distinct_id — it forks the identity space into a third id namespace.
          if (data.user?.id) posthog.identify(data.user.id, { email });
          // Acquisition attribution: forward this account's captured first-touch
          // (UTM + click ids) to the server so it lands on the profile, and fire
          // the TikTok CompleteRegistration conversion. The shared eventId dedups
          // the client pixel fire against the server Events API fire.
          const firstTouch = readFirstTouch();
          // Deterministic per-account eventId — the server CompleteRegistration
          // uses the same `signup_<userId>`, so the client pixel and the server
          // Events API fires always dedup to a single TikTok conversion, even on
          // a retry from a fresh page load.
          const registrationEventId = data.user?.id
            ? `signup_${data.user.id}`
            : typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
              ? crypto.randomUUID()
              : `signup_${email}`;
          trackTikTokClientEvent("CompleteRegistration", {}, registrationEventId);
          // signup_completed is emitted server-side by the user-created-log
          // handler (one authoritative fire). Do NOT also fire it client-side or
          // it double-counts (1 client + 1 server) for the same account.
          invokeProtectedFunction("user-created-log", {
            body: { source: "auth_sign_up", firstTouch, eventId: registrationEventId },
          })
            .catch((invokeError) => {
              console.log("user-created-log invoke failed:", invokeError);
            });
          navigate(nextPath, { replace: true });
        } else {
          // No session yet (email confirmation required). The server handler only
          // runs once the account is active, so this client fire is the only
          // record of the signup *intent*; tag it distinctly so confirmed signups
          // (server, source "auth_sign_up") stay separable from unconfirmed intents.
          if (data.user?.id) posthog.identify(data.user.id, { email });
          trackClientEvent("signup_completed", { source: "email_unconfirmed" });
          setMessage("Check your email to confirm your account.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    setError(null);
    setMessage(null);
    if (!mfaFactorId) {
      setError("Select an MFA factor.");
      return;
    }
    if (!mfaCode.trim()) {
      setError("Enter your MFA code.");
      return;
    }

    setLoading(true);
    try {
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaFactorId,
        code: mfaCode.trim(),
      });
      if (verifyError) {
        setError(verifyError.message);
        return;
      }
      trackClientEvent("login_completed", { flow: "mfa" });
      navigate(nextPath, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setMessage(null);
    setResetEmailSent(false);

    if (!email) {
      setError("Enter your email to reset your password.");
      return;
    }

    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback?flow=recovery&next=${encodeURIComponent("/reset-password")}`,
      });
      // Supabase deliberately never tells the client whether the email matched
      // an account, so we can only record whether the request itself errored.
      trackClientEvent("password_reset_requested", { had_error: Boolean(resetError) });
      if (resetError) {
        setError(resetError.message);
        return;
      }
      // Existence-agnostic on purpose (no account enumeration). Also steers the
      // two biggest "I never got the email" groups: Discord sign-ups (no password
      // to reset) and people opening the single-use link on a different device.
      setMessage(
        "If an account exists for that email, a reset link is on its way — check your inbox and spam. " +
          "Signed up with Discord? Use “Continue with Discord” instead; Discord accounts don’t have a password to reset. " +
          "Open the link soon, on this same device and browser — it’s single-use and expires quickly.",
      );
      setResetEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscordAuth = async () => {
    setError(null);
    setMessage(null);
    setLoading(true);
    trackClientEvent("login_with_discord_clicked", { mode });
    try {
      const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextPath)}`;
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: "discord",
        options: {
          redirectTo,
          scopes: "identify email",
          skipBrowserRedirect: true,
        } as never,
      });

      const redirectUrl = data?.url ?? null;
      if (oauthError || !redirectUrl) {
        setError(oauthError?.message ?? "Failed to start Discord sign-in.");
        return;
      }

      window.location.assign(redirectUrl);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="premium-shell min-h-screen text-foreground">
      <div className="absolute inset-x-0 top-0 -z-10 h-[540px] bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.18),transparent_58%)]" />
      <div className="absolute inset-y-0 left-0 -z-10 w-full bg-[linear-gradient(180deg,hsl(var(--background)/0.98),hsl(var(--background)/0.82)_28%,hsl(24_18%_6%/0.96))]" />

      <main className="w-full px-4 pb-14 pt-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
          <header className="rounded-[2rem] border border-border/60 bg-foreground/[0.04] shadow-[0_30px_120px_rgba(2,6,23,0.45)] backdrop-blur-xl">
            <div className="flex flex-col gap-6 px-5 py-5 sm:px-7 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary)/0.08)]">
                  <Hammer className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-semibold tracking-tight">Workshop Access</p>
                  <p className="text-sm text-muted-foreground">
                    Sign in or create an account using the same secure platform controls.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  asChild
                  variant="ghost"
                  className="h-10 rounded-full border border-border/60 bg-foreground/[0.03] px-4 text-sm text-foreground hover:bg-foreground/[0.06]"
                >
                  <Link to="/">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to site
                  </Link>
                </Button>
                <ThemeToggle />
              </div>
            </div>
          </header>

          <section className="mx-auto w-full max-w-[34rem] rounded-[1.75rem] border border-border/60 bg-foreground/[0.03] p-6 space-y-5">
                <div className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-primary/80">
                    {mode === "sign-in" ? "Returning user" : "New account"}
                  </p>
                  <h2 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h2>
                  <p className="text-sm leading-6 text-muted-foreground">
                    {mode === "sign-in"
                      ? "Welcome back. Use your account to continue."
                      : "Create an account to save and manage your work."}
                  </p>
                </div>

        <form className="space-y-3.5" onSubmit={handleSubmit}>
          {mode === "sign-up" && (
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground flex items-center gap-2" htmlFor="displayName">
                <Hammer className="h-3.5 w-3.5 text-primary" />
                Display name
              </label>
              <Input
                id="displayName"
                type="text"
                name="display_name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Workshop Builder"
                autoComplete="nickname"
                autoCapitalize="words"
                autoCorrect="off"
                className="pr-12"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-2" htmlFor="email">
              <Mail className="h-3.5 w-3.5 text-primary" />
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@workshop.dev"
              autoComplete="email"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-2" htmlFor="password">
              <Lock className="h-3.5 w-3.5 text-primary" />
              Password
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
              autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
            />
            {mode === "sign-up" && (
              <p className="text-[11px] text-muted-foreground">
                Use 8+ characters with at least one uppercase letter, one lowercase letter, and one number.
              </p>
            )}
          </div>
          {mode === "sign-up" && (
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground flex items-center gap-2" htmlFor="confirm">
                <Lock className="h-3.5 w-3.5 text-primary" />
                Confirm password
              </label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
          )}

          {error && (
            <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 space-y-1">
              <p>{error}</p>
              {showPasswordRequirements && (
                <ul className="text-[11px] list-disc list-inside space-y-0.5">
                  <li>At least 8 characters</li>
                  <li>At least one uppercase letter (A-Z)</li>
                  <li>At least one lowercase letter (a-z)</li>
                  <li>At least one number (0-9)</li>
                </ul>
              )}
            </div>
          )}
          {message && (
            <div className="text-xs text-muted-foreground bg-muted/40 border border-border/50 rounded-md px-3 py-2">
              {message}
            </div>
          )}

          {mode === "sign-up" ? (
            <div className="space-y-2">
              <Button className="h-10 w-full text-sm" type="submit" disabled={loading}>
                {loading ? "Working..." : "Create account"}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Button className="h-10 w-full text-sm" type="submit" disabled={loading}>
                {loading ? "Working..." : "Sign in"}
              </Button>
              {mfaRequired && (
                <div className="space-y-2 rounded-md border border-border/50 bg-muted/20 p-3">
                  <label className="text-xs text-muted-foreground" htmlFor="mfaFactor">
                    Authenticator
                  </label>
                  <select
                    id="mfaFactor"
                    value={mfaFactorId}
                    onChange={(event) => setMfaFactorId(event.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {mfaFactors.map((factor) => (
                      <option key={factor.id} value={factor.id}>
                        {factor.label}
                      </option>
                    ))}
                  </select>
                  <label className="text-xs text-muted-foreground" htmlFor="mfaCode">
                    MFA code
                  </label>
                  <Input
                    id="mfaCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value)}
                    placeholder="123456"
                  />
                  <Button type="button" className="h-10 w-full text-sm" onClick={handleMfaVerify} disabled={loading}>
                    {loading ? "Verifying..." : "Verify MFA & Continue"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </form>

        <div className="space-y-3">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border/60" />
            <span>or continue with</span>
            <div className="h-px flex-1 bg-border/60" />
          </div>
          <Button type="button" variant="outline" className="h-10 w-full text-sm" disabled={loading} onClick={handleDiscordAuth}>
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="currentColor"
            >
              <path d="M20.317 4.369A19.79 19.79 0 0 0 15.425 3c-.212.375-.46.873-.63 1.264a18.27 18.27 0 0 0-5.592 0A12.64 12.64 0 0 0 8.57 3a19.736 19.736 0 0 0-4.892 1.37C.587 9.02-.246 13.555.171 18.03a19.9 19.9 0 0 0 5.993 3.03c.486-.664.918-1.37 1.288-2.116a12.96 12.96 0 0 1-2.028-.97c.169-.125.335-.255.496-.388 3.91 1.838 8.154 1.838 12.018 0 .162.133.327.263.496.388-.645.378-1.323.703-2.028.97.37.746.802 1.452 1.288 2.116a19.87 19.87 0 0 0 5.993-3.03c.49-5.204-.837-9.697-3.374-13.66ZM8.02 15.33c-1.183 0-2.156-1.08-2.156-2.407 0-1.326.946-2.407 2.156-2.407 1.218 0 2.174 1.09 2.156 2.407 0 1.326-.946 2.407-2.156 2.407Zm7.957 0c-1.183 0-2.156-1.08-2.156-2.407 0-1.326.946-2.407 2.156-2.407 1.218 0 2.174 1.09 2.156 2.407 0 1.326-.938 2.407-2.156 2.407Z" />
            </svg>
            {mode === "sign-in" ? "Continue with Discord" : "Sign up with Discord"}
          </Button>
        </div>

        {mode === "sign-in" && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={handleForgotPassword}
              disabled={loading || resetEmailSent}
            >
              {resetEmailSent ? "Reset email sent" : "Forgot password?"}
            </button>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          {mode === "sign-in" ? "Need an account?" : "Already have an account?"}{" "}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setMode(mode === "sign-in" ? "sign-up" : "sign-in")}
          >
            {mode === "sign-in" ? "Create one" : "Sign in"}
          </button>
        </div>
          </section>
        </div>
      </main>
    </div>
  );
}
